import { z } from 'zod';
import { zfd } from 'zod-form-data';
import type { ActionFunction } from '~/types/route-args';
import { UserErrorResponse } from '~/modules/root/graphql/responses';
import type { UpdateShoppingCartItemMutationVariables } from 'generated/graphql';
import { updateShoppingCartItem } from '@modules/ecommerce/api/update-shopping-cart-item.server';

export const UPDATE_SHOPPING_CART_ITEM_API_ROUTE =
    '/remix-api/ecommerce/update-shopping-cart-item';

/**
 * A missing `cartItemId` used to arrive as `Number(null)` — zero — which the
 * resolver looked up, found nothing for, and then dereferenced, so an
 * unauthenticated POST with no body was a fatal rather than a 400. Quantity
 * is bounded server-side as well; zero is the documented way to take a line
 * out of the cart, so it is allowed through.
 */
const requestValidator = z.object({
    cartItemId: zfd.numeric(z.number().int().positive()),
    quantity: zfd.numeric(z.number().int().gte(0)).optional(),
});

/** Sets the quantity on one cart line. */
export const action: ActionFunction = async ({ request, context }) => {
    const result = zfd
        .formData(requestValidator)
        .safeParse(await request.formData());

    if (!result.success) {
        throw new UserErrorResponse(result.error);
    }

    const variables: UpdateShoppingCartItemMutationVariables = {
        cartItemId: result.data.cartItemId,
    };
    if (result.data.quantity !== undefined) {
        variables.quantity = result.data.quantity;
    }

    return updateShoppingCartItem({ ...context, variables });
};

export type EcommerceUpdateShoppingCartItemAction = typeof action;
